import {
  OwcTypography,
  OwcTextarea,
  OwcSlideToggle,
  OwcCheckbox,
} from "@one/react";
import { useFormikContext } from "formik";
import PropTypes from "prop-types";

const ToggleDeclaration = ({
  title,
  toggleLabel,
  toggleSubtext,
  fieldName,
  textFieldName,
  textareaPlaceholder,
  textareaMaxLength = 200,
  textareaRows = 3,
  toggleType = "slide", // "slide" or "checkbox"
  invertToggleLogic = false, // for hazardous materials, we want true when toggle is OFF
  containerStyle = {},
  titleStyle = {},
  toggleContainerStyle = {},
  textareaStyle = {},
  alwaysShowTextarea = false,
  disableTextAreaWhenToggledOff = false,
}) => {
  const formik = useFormikContext();

  const handleToggleChange = () => {
    const currentValue = formik.values[fieldName];
    const nextValue = !currentValue;
    formik.setFieldValue(fieldName, nextValue);

    // Clear textarea when appropriate
    if ((invertToggleLogic && !nextValue) || (!invertToggleLogic && !nextValue)) {
      formik.setFieldValue(textFieldName, "");
    }
  };

  const handleTextareaChange = (value) => {
    formik.setFieldValue(textFieldName, value);
  };

  const isToggleChecked = invertToggleLogic
    ? !formik.values[fieldName]
    : !!formik.values[fieldName];

  const showTextarea = (alwaysShowTextarea) || (invertToggleLogic
    ? formik.values[fieldName]
    : formik.values[fieldName]);

  const ToggleComponent = toggleType === "checkbox" ? OwcCheckbox : OwcSlideToggle;

  return (
    <div style={{ paddingBottom: "20px", ...containerStyle }}>
      {title && (
        <OwcTypography
          variant="subtitle2"
          style={{ marginBottom: "15px", fontWeight: "500", ...titleStyle }}
        >
          {title}
        </OwcTypography>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px", ...toggleContainerStyle }}>
        <ToggleComponent
          errorText={formik.errors[fieldName]}
          data-testid={`${fieldName}-toggle`}
          checked={isToggleChecked}
          onInputChange={handleToggleChange}
          style={{
            marginRight: toggleType === "slide" ? "10px" : "12px",
            marginTop: toggleType === "slide" ? "2px" : "0"
          }}
        >
          {toggleType === "checkbox" && toggleLabel}
        </ToggleComponent>

        {toggleType === "slide" && (
          <div>
            <OwcTypography variant="body1" style={{ fontWeight: "500", marginBottom: "5px" }}>
              {toggleLabel}
            </OwcTypography>
            {toggleSubtext && (
              <OwcTypography variant="body2" style={{ color: "#666", fontSize: "14px" }}>
                {toggleSubtext}
              </OwcTypography>
            )}
          </div>
        )}
      </div>

      {showTextarea && (
        <OwcTextarea
          name={textFieldName}
          data-testid={`${textFieldName}-field`}
          placeholder={textareaPlaceholder}
          value={formik.values[textFieldName] || ""}
          onBlur={formik.handleBlur}
          onInputKeyDown={(ev) => handleTextareaChange(ev.detail)}
          onInputChange={(ev) => handleTextareaChange(ev.detail)}
          onInputReset={() => formik.setFieldValue(textFieldName, "", true)}
          maxLength={textareaMaxLength}
          minLength={0}
          disabled={disableTextAreaWhenToggledOff && !isToggleChecked}
          cols="18"
          rows={textareaRows}
          reset
          errorText={formik.errors[textFieldName]}
          validation-mode="realtime"
          required
          style={textareaStyle}
        />
      )}
    </div>
  );
};

ToggleDeclaration.propTypes = {
  title: PropTypes.string,
  toggleLabel: PropTypes.string.isRequired,
  toggleSubtext: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  textFieldName: PropTypes.string.isRequired,
  textareaPlaceholder: PropTypes.string.isRequired,
  textareaMaxLength: PropTypes.number,
  textareaRows: PropTypes.number,
  toggleType: PropTypes.oneOf(["slide", "checkbox"]),
  invertToggleLogic: PropTypes.bool,
  containerStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  toggleContainerStyle: PropTypes.object,
  textareaStyle: PropTypes.object,
};

export default ToggleDeclaration;
